/**
 * On-call mobile/tablet clinical triage profile (Epic D1 / Gap #15).
 * Extends EEG-MOB-001 read-only mode with limited ack/defer/escalate actions,
 * MFA session rules, and notification → triage → ack audit loop.
 *
 * Intended use: secure waveform candidate triage + jump-to-raw windows.
 * Excluded: full diagnostic interpretation, annotation editing, report signing,
 * closed-loop therapy, or uncleared standalone diagnostic-device claims.
 */

import {
  createMobileReadOnlyMode,
  isMobileControlAllowed,
  type MobileReviewMode,
} from "@features/Support/utils/supportLoop";

export const MOBILE_TRIAGE_SCHEMA_VERSION = "mobile-triage-v1.0.0";

export const MOBILE_TRIAGE_PHASE_CONSTRAINTS = {
  phase: "assistive_triage" as const,
  allowedClaims: [
    "Review AI/detector candidates on a secured session",
    "Jump to raw EEG windows for visual confirmation",
    "Acknowledge, defer, or escalate candidates with audit",
  ],
  excludedClaims: [
    "Standalone mobile diagnostic device claim",
    "FDA/CE diagnostic equivalence without cleared indications",
    "Report signing or full interpretation on mobile",
    "Closed-loop therapy or stimulation control",
  ],
  clinicalUseNotice:
    "This profile is an assistive on-call triage surface with release-phase constraints; it supports candidate review, raw-window jump, acknowledge, defer, and escalation workflows, and does not assert standalone diagnostic clearance or FDA/CE diagnostic equivalence.",
};

export type MobileTriageAction =
  | "seek"
  | "play_pause"
  | "montage_select"
  | "filter_view"
  | "jump_event"
  | "ack_candidate"
  | "defer_candidate"
  | "escalate_candidate";

export type MobileTriageBlocked =
  | "annotate"
  | "edit_report"
  | "artifact_reduction"
  | "sign_report"
  | "full_diagnostic_claim";

export type MobileTriageProfile = Omit<MobileReviewMode, "blockedControls"> & {
  schemaVersion: string;
  mfaSatisfied: boolean;
  sessionMaxMinutes: number;
  sessionExpiresAtIso: string | null;
  triageActions: MobileTriageAction[];
  blockedControls: MobileTriageBlocked[];
  intendedUseClass: "assistive_triage";
  phaseConstraints: typeof MOBILE_TRIAGE_PHASE_CONSTRAINTS;
};

export type MobileTriageAuditEntry = {
  atIso: string;
  actorId: string;
  action: "notify" | "open" | "ack" | "defer" | "escalate" | "session_deny";
  studyId: string;
  candidateId?: string | null;
  notificationId?: string | null;
  deepLink?: string | null;
  note?: string | null;
};

export type MobileTriageDeepLink = {
  path: string;
  studyId: string;
  candidateId?: string | null;
  notificationId?: string | null;
  audit: MobileTriageAuditEntry;
};

const DEFAULT_SESSION_MAX_MINUTES = 30;

export function createMobileTriageProfile(input: {
  enabled: boolean;
  mfaSatisfied: boolean;
  sessionMaxMinutes?: number;
  now?: Date;
}): MobileTriageProfile {
  const base = createMobileReadOnlyMode(input.enabled);
  const maxMinutes = Math.max(5, input.sessionMaxMinutes ?? DEFAULT_SESSION_MAX_MINUTES);
  const now = input.now ?? new Date();
  const sessionActive = input.enabled && input.mfaSatisfied;
  const expires = sessionActive
    ? new Date(now.getTime() + maxMinutes * 60_000).toISOString()
    : null;

  return {
    ...base,
    enabled: input.enabled,
    readOnly: true,
    schemaVersion: MOBILE_TRIAGE_SCHEMA_VERSION,
    mfaSatisfied: input.mfaSatisfied,
    sessionMaxMinutes: maxMinutes,
    sessionExpiresAtIso: expires,
    allowedControls: base.allowedControls,
    triageActions: sessionActive
      ? [
          "seek",
          "play_pause",
          "montage_select",
          "filter_view",
          "jump_event",
          "ack_candidate",
          "defer_candidate",
          "escalate_candidate",
        ]
      : ["seek", "play_pause", "jump_event"],
    blockedControls: [
      "annotate",
      "edit_report",
      "artifact_reduction",
      "sign_report",
      "full_diagnostic_claim",
    ],
    intendedUseClass: "assistive_triage",
    phaseConstraints: MOBILE_TRIAGE_PHASE_CONSTRAINTS,
  };
}

export function isMobileTriageSessionActive(
  profile: MobileTriageProfile,
  now: Date = new Date(),
): boolean {
  if (!profile.enabled || !profile.mfaSatisfied) return false;
  if (!profile.sessionExpiresAtIso) return false;
  return new Date(profile.sessionExpiresAtIso).getTime() > now.getTime();
}

export function isMobileTriageActionAllowed(
  profile: MobileTriageProfile,
  action: string,
  now: Date = new Date(),
): boolean {
  if (!profile.enabled) return true;
  if (profile.blockedControls.includes(action as MobileTriageBlocked)) {
    return false;
  }
  if (!isMobileTriageSessionActive(profile, now)) {
    return isMobileControlAllowed(
      {
        ...profile,
        blockedControls: profile.blockedControls.filter(
          (control): control is MobileReviewMode["blockedControls"][number] =>
            control !== "full_diagnostic_claim",
        ),
      },
      action,
    );
  }
  return profile.triageActions.includes(action as MobileTriageAction);
}

export function buildMobileTriageDeepLink(input: {
  studyId: string;
  candidateId?: string | null;
  notificationId?: string | null;
  actorId: string;
  now?: Date;
}): MobileTriageDeepLink {
  const params = new URLSearchParams({
    mobileTriage: "1",
    studyId: input.studyId,
  });
  if (input.candidateId) params.set("candidateId", input.candidateId);
  if (input.notificationId) params.set("notificationId", input.notificationId);
  const path = `/worklist?${params.toString()}`;
  const atIso = (input.now ?? new Date()).toISOString();
  return {
    path,
    studyId: input.studyId,
    candidateId: input.candidateId ?? null,
    notificationId: input.notificationId ?? null,
    audit: {
      atIso,
      actorId: input.actorId,
      action: "notify",
      studyId: input.studyId,
      candidateId: input.candidateId ?? null,
      notificationId: input.notificationId ?? null,
      deepLink: path,
    },
  };
}

export function recordMobileTriageDisposition(input: {
  profile: MobileTriageProfile;
  actorId: string;
  studyId: string;
  candidateId: string;
  disposition: "ack" | "defer" | "escalate";
  note?: string | null;
  now?: Date;
}): { ok: boolean; audit: MobileTriageAuditEntry; reason?: string } {
  const now = input.now ?? new Date();
  const actionMap = {
    ack: "ack_candidate",
    defer: "defer_candidate",
    escalate: "escalate_candidate",
  } as const;
  if (!isMobileTriageActionAllowed(input.profile, actionMap[input.disposition], now)) {
    return {
      ok: false,
      reason: "session_or_mfa_required",
      audit: {
        atIso: now.toISOString(),
        actorId: input.actorId,
        action: "session_deny",
        studyId: input.studyId,
        candidateId: input.candidateId,
        note: input.note ?? null,
      },
    };
  }
  return {
    ok: true,
    audit: {
      atIso: now.toISOString(),
      actorId: input.actorId,
      action: input.disposition,
      studyId: input.studyId,
      candidateId: input.candidateId,
      note: input.note ?? null,
    },
  };
}

export const MOBILE_DEVICE_BROWSER_MATRIX = [
  {
    platform: "iOS",
    browser: "Safari",
    minVersion: "16+",
    support: "certified" as const,
    notes: "Primary tablet triage; MFA via WebAuthn/SSO",
  },
  {
    platform: "iOS",
    browser: "Chrome",
    minVersion: "120+",
    support: "best_effort" as const,
    notes: "Uses WebKit; validate MFA cookies",
  },
  {
    platform: "Android",
    browser: "Chrome",
    minVersion: "120+",
    support: "certified" as const,
    notes: "Primary phone/tablet triage",
  },
  {
    platform: "Android",
    browser: "Samsung Internet",
    minVersion: "23+",
    support: "best_effort" as const,
    notes: "Smoke a11y + deep-link only",
  },
  {
    platform: "iPadOS",
    browser: "Safari",
    minVersion: "16+",
    support: "certified" as const,
    notes: "Landscape waveform triage viewport",
  },
] as const;
